import { z } from 'zod'

const coerceRecord = z.preprocess((v) => {
  if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
    return v
  }
  return {}
}, z.record(z.string(), z.unknown()))

/** PHP `[]` encodes empty maps as JSON arrays - accept both `{}` and `[]`. */
const coerceStringRecord = z.preprocess((v) => {
  if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
    return v
  }
  return {}
}, z.record(z.string(), z.string()))

const portalNavigationLeafSchema = z.object({
  id: z.string().regex(/^[a-z0-9][a-z0-9_-]{2,63}$/i),
  label: z.string().trim().min(1).max(120),
  sort: z.number(),
  enabled: z.boolean(),
  link_type: z.enum(['internal', 'external']),
  href: z.string(),
  placement: z.enum(['header_primary', 'footer_legal_secondary']),
  module_slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).optional(),
  link_title: z.string().max(180).optional(),
  target: z.enum(['_self', '_blank', '_parent', '_top']).optional(),
  rel: z.string().optional(),
  children: z.array(z.never()).max(0).optional(),
}).superRefine((item, context) => {
  if (item.link_type === 'internal' && (!item.href.startsWith('/') || item.href.startsWith('//') || /\s/u.test(item.href))) {
    context.addIssue({ code: 'custom', path: ['href'], message: 'invalid_internal_path' })
  }
  if (item.link_type === 'external') {
    try {
      if (new URL(item.href).protocol !== 'https:') {
        context.addIssue({ code: 'custom', path: ['href'], message: 'invalid_external_url' })
      }
    } catch {
      context.addIssue({ code: 'custom', path: ['href'], message: 'invalid_external_url' })
    }
  }
})

export const portalNavigationItemSchema = z.object({
  id: z.string().regex(/^[a-z0-9][a-z0-9_-]{2,63}$/i),
  label: z.string().trim().min(1).max(120),
  sort: z.number(),
  enabled: z.boolean(),
  link_type: z.enum(['internal', 'external', 'group']),
  href: z.string(),
  placement: z.enum(['header_primary', 'footer_legal_secondary']),
  module_slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).optional(),
  link_title: z.string().max(180).optional(),
  target: z.enum(['_self', '_blank', '_parent', '_top']).optional(),
  rel: z.string().optional(),
  children: z.array(portalNavigationLeafSchema).max(12).default([]),
}).superRefine((item, context) => {
  if (item.link_type === 'group') {
    if (item.href !== '' || item.children.length === 0) {
      context.addIssue({ code: 'custom', path: ['children'], message: 'invalid_group' })
    }
    return
  }
  if (item.link_type === 'internal' && (!item.href.startsWith('/') || item.href.startsWith('//') || /\s/u.test(item.href))) {
    context.addIssue({ code: 'custom', path: ['href'], message: 'invalid_internal_path' })
  }
  if (item.link_type === 'external') {
    try {
      if (new URL(item.href).protocol !== 'https:') {
        context.addIssue({ code: 'custom', path: ['href'], message: 'invalid_external_url' })
      }
    } catch {
      context.addIssue({ code: 'custom', path: ['href'], message: 'invalid_external_url' })
    }
  }
})

export type PortalNavigationItem = {
  id: string
  label: string
  sort: number
  enabled: boolean
  link_type: 'internal' | 'external' | 'group'
  href: string
  placement: 'header_primary' | 'footer_legal_secondary'
  module_slug?: string
  link_title?: string
  target?: string
  rel?: string
  children: PortalNavigationItem[]
}

/** DTO appearance fields from GET …/brand (public; extra snapshot keys are stripped). */
export const brandPublicAppearanceDataSchema = z.object({
  name: z.string().optional(),
  theme_slug: z.string().optional(),
  theme_palette_override_enabled: z.boolean().optional(),
  logo_url: z.string().nullable().optional(),
  logo_dark_url: z.string().nullable().optional(),
  logo_favicon_url: z.string().nullable().optional(),
  colors: coerceRecord.optional(),
  typography: coerceRecord.optional(),
  radius_preset: z.string().optional(),
  custom_tokens: coerceStringRecord.optional(),
  ui_preferences: coerceRecord.optional(),
})

export const brandPublicAppearanceResponseSchema = z.object({
  data: brandPublicAppearanceDataSchema,
})

export type BrandPublicAppearance = z.infer<typeof brandPublicAppearanceDataSchema>

/** DTO from GET …/brand (full public snapshot: navigation, contact, ui preferences). */
export const brandAuthenticatedDataSchema = brandPublicAppearanceDataSchema.extend({
  colors: coerceRecord.optional(),
  contact: coerceRecord.optional(),
  portal_navigation: z.array(z.record(z.string(), z.unknown())).optional(),
  ui_preferences: coerceRecord.optional(),
})

export const brandAuthenticatedApiResponseSchema = z.object({
  data: brandAuthenticatedDataSchema,
})

export type BrandAuthenticatedSnapshot = z.infer<typeof brandAuthenticatedDataSchema>

/** Objet utilisé pour thème CSS (nom, logo, couleurs fusionnées). */
export type BrandShellAppearance = BrandPublicAppearance

function coercePortalNavigation(
  rows: BrandAuthenticatedSnapshot['portal_navigation'],
): PortalNavigationItem[] {
  const raw = (Array.isArray(rows) ? rows : []).map((row, index) => {
    const legacySeed = typeof row.href === 'string' ? row.href : typeof row.label === 'string' ? row.label : String(index)
    const legacyId = `legacy-${index}-${legacySeed}`
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 63)

    return {
      ...row,
      id: typeof row.id === 'string' ? row.id : legacyId,
      children: Array.isArray(row.children) ? row.children : [],
    }
  })
  const parsed = z.array(portalNavigationItemSchema).max(20).safeParse(raw)
  if (!parsed.success) return []

  const ids = parsed.data.flatMap((item) => [item.id, ...item.children.map((child) => child.id)])
  if (ids.length > 50 || new Set(ids).size !== ids.length) return []

  return parsed.data.map((item) => ({
    ...item,
    children: item.children.map((child) => ({ ...child, children: [] })),
  }))
}

export function parseAuthenticatedPortalNavigation(
  snapshot: BrandAuthenticatedSnapshot | undefined,
): PortalNavigationItem[] {
  if (snapshot == null || snapshot.portal_navigation == null) {
    return []
  }
  return coercePortalNavigation(snapshot.portal_navigation)
}

/**
 * Flag UI piloté par l'admin (brand_settings.ui_preferences.gallery_lightbox).
 * Défaut **true** : la galerie plein écran est active tant que l'admin ne l'a pas
 * désactivée (ou si le snapshot n'est pas encore chargé).
 */
export function isGalleryLightboxEnabled(snapshot: BrandAuthenticatedSnapshot | undefined): boolean {
  const raw = snapshot?.ui_preferences?.gallery_lightbox
  return raw === undefined ? true : raw !== false
}

/** brand_settings.ui_preferences.show_logo — default true; false hides logo and fallback image. */
export function isShowLogoEnabled(snapshot: BrandAuthenticatedSnapshot | undefined): boolean {
  const raw = snapshot?.ui_preferences?.show_logo
  return raw === undefined ? true : raw !== false
}

/**
 * Reveal the pre-subsidy / base price (brand_settings.ui_preferences.show_unsubsidized_price).
 * Defaults to **false** - property pages show the final nightly rate only until an admin opts in,
 * and while the snapshot is still loading. When on, the front may show the base amount struck through.
 */
export function isUnsubsidizedPriceVisible(snapshot: BrandAuthenticatedSnapshot | undefined): boolean {
  return snapshot?.ui_preferences?.show_unsubsidized_price === true
}

/**
 * Show “après subvention” / subsidy % wording (brand_settings.ui_preferences.show_subsidy_mention).
 * Defaults to **false** - plain price labels until an admin opts in.
 */
export function isSubsidyMentionVisible(snapshot: BrandAuthenticatedSnapshot | undefined): boolean {
  return snapshot?.ui_preferences?.show_subsidy_mention === true
}

/** CR-14: show nightly price under calendar days. Default true. */
export function isCalendarShowPrices(snapshot: BrandAuthenticatedSnapshot | undefined): boolean {
  const raw = snapshot?.ui_preferences?.calendar_show_prices
  return raw === undefined ? true : raw !== false
}

export type CalendarMobileLayoutPref = 'hybrid' | 'inline' | 'sheet'

/** CR-14: mobile calendar presentation. Default hybrid. */
export function calendarMobileLayoutFromSnapshot(
  snapshot: BrandAuthenticatedSnapshot | undefined,
): CalendarMobileLayoutPref {
  const raw = snapshot?.ui_preferences?.calendar_mobile_layout
  if (raw === 'inline' || raw === 'sheet' || raw === 'hybrid') return raw
  return 'hybrid'
}

/**
 * CR-14: checkout-only / turnaround half-days. Default true.
 * Property override (when provided) wins over brand preference.
 */
export function isCalendarTurnaroundEnabled(
  snapshot: BrandAuthenticatedSnapshot | undefined,
  propertyOverride?: boolean | null,
): boolean {
  if (propertyOverride === true || propertyOverride === false) return propertyOverride
  const raw = snapshot?.ui_preferences?.calendar_turnaround_enabled
  return raw === undefined ? true : raw !== false
}

export type CalendarColorKey =
  | 'available'
  | 'low'
  | 'full'
  | 'annotated'
  | 'season_closed'
  | 'outside_window'
  | 'lead_time'

const CALENDAR_COLOR_KEYS: readonly CalendarColorKey[] = [
  'available',
  'low',
  'full',
  'annotated',
  'season_closed',
  'outside_window',
  'lead_time',
] as const

const HEX6 = /^#[0-9A-Fa-f]{6}$/

/**
 * CR-14: optional hex overrides for calendar day statuses.
 * Invalid / missing keys are omitted (FE falls back to CSS tokens).
 */
export function calendarColorsFromSnapshot(
  snapshot: BrandAuthenticatedSnapshot | undefined,
): Partial<Record<CalendarColorKey, string>> {
  const raw = snapshot?.ui_preferences?.calendar_colors
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {}
  const out: Partial<Record<CalendarColorKey, string>> = {}
  for (const key of CALENDAR_COLOR_KEYS) {
    const v = (raw as Record<string, unknown>)[key]
    if (typeof v === 'string' && HEX6.test(v.trim())) {
      out[key] = v.trim()
    }
  }
  return out
}

export type OnboardingStepGradientKey =
  | 'welcome'
  | 'housing'
  | 'events'
  | 'profile'
  | 'notifications'
  | 'legal'
  | 'final'

const ONBOARDING_STEP_GRADIENT_KEYS = [
  'welcome',
  'housing',
  'events',
  'profile',
  'notifications',
  'legal',
  'final',
] as const satisfies readonly OnboardingStepGradientKey[]

/**
 * RV-30: optional CSS gradients for full-screen onboarding editorial panels.
 * Admin-configured via brand_settings.ui_preferences.onboarding.step_gradients.
 */
export function onboardingStepGradientsFromSnapshot(
  snapshot: BrandAuthenticatedSnapshot | undefined,
): Partial<Record<OnboardingStepGradientKey, string>> {
  const onboarding = snapshot?.ui_preferences?.onboarding
  if (typeof onboarding !== 'object' || onboarding === null || Array.isArray(onboarding)) {
    return {}
  }
  const gradients = (onboarding as Record<string, unknown>).step_gradients
  if (typeof gradients !== 'object' || gradients === null || Array.isArray(gradients)) {
    return {}
  }
  const out: Partial<Record<OnboardingStepGradientKey, string>> = {}
  for (const key of ONBOARDING_STEP_GRADIENT_KEYS) {
    const v = (gradients as Record<string, unknown>)[key]
    if (typeof v !== 'string') {
      continue
    }
    const trimmed = v.trim()
    if (trimmed === '' || trimmed.length > 500) {
      continue
    }
    if (!trimmed.toLowerCase().includes('gradient(')) {
      continue
    }
    if (/url\s*\(|expression\s*\(|@import|javascript:|<|>/i.test(trimmed)) {
      continue
    }
    out[key] = trimmed
  }
  return out
}

const DEFAULT_AVAILABILITY_MAX_SPAN_DAYS = 365

/** CR-14: max calendar availability query window (reservation_global_settings). Default 365. */
export function availabilityMaxSpanDaysFromSnapshot(
  snapshot: BrandAuthenticatedSnapshot | undefined,
): number {
  const raw = snapshot?.ui_preferences?.availability_max_span_days
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 1) {
    return Math.trunc(raw)
  }
  if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) {
    const parsed = Number.parseInt(raw.trim(), 10)
    return parsed >= 1 ? parsed : DEFAULT_AVAILABILITY_MAX_SPAN_DAYS
  }

  return DEFAULT_AVAILABILITY_MAX_SPAN_DAYS
}

export type HomeLayout = 'hub' | 'cards'

/**
 * Mise en page de l'accueil pilotée par l'admin (brand_settings.ui_preferences.home_layout).
 * Défaut **'hub'** (hub dense signé CSE) : les cartes 3 colonnes sont un opt-in admin (V3-P2).
 */
export function homeLayoutFromSnapshot(snapshot: BrandAuthenticatedSnapshot | undefined): HomeLayout {
  return snapshot?.ui_preferences?.home_layout === 'cards' ? 'cards' : 'hub'
}

export type PartnerOfferSliderPosition = 'off' | 'top' | 'bottom'

/**
 * T6.4 - home slider placement for featured PartnerOffer items, admin-configurable
 * (brand_settings.ui_preferences.partner_offer_slider_position). Defaults **'off'** - the module
 * itself defaults disabled (`PARTNER_OFFER_ENABLED=false`), and an unset preference must never
 * surface a slider the admin never opted into.
 */
export function partnerOfferSliderPositionFromSnapshot(
  snapshot: BrandAuthenticatedSnapshot | undefined,
): PartnerOfferSliderPosition {
  const raw = snapshot?.ui_preferences?.partner_offer_slider_position
  return raw === 'top' || raw === 'bottom' ? raw : 'off'
}

export type MeNavLinksPref = {
  group_trip: boolean
  partner_offer: boolean
  colony: boolean
  listing: boolean
}

const DEFAULT_ME_NAV_LINKS: MeNavLinksPref = {
  group_trip: true,
  partner_offer: true,
  colony: true,
  listing: true,
}

/**
 * Profile sidebar optional module links (brand_settings.ui_preferences.me_nav_links).
 * Missing keys default to visible (retrocompat).
 */
export function meNavLinksFromSnapshot(
  snapshot: BrandAuthenticatedSnapshot | undefined,
): MeNavLinksPref {
  const raw = snapshot?.ui_preferences?.me_nav_links
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...DEFAULT_ME_NAV_LINKS }
  }
  const row = raw as Record<string, unknown>
  return {
    group_trip: row.group_trip === false ? false : true,
    partner_offer: row.partner_offer === false ? false : true,
    colony: row.colony === false ? false : true,
    listing: row.listing === false ? false : true,
  }
}

export function isMeNavLinkVisible(
  snapshot: BrandAuthenticatedSnapshot | undefined,
  module: keyof MeNavLinksPref,
): boolean {
  return meNavLinksFromSnapshot(snapshot)[module]
}

/** CGV/CGU consent mode (ui_preferences.legal_consent.mode, legacy require_cgv_consent). */
export type RequireCgvConsent = 'required' | 'optional' | 'off'

export type LegalConsentGatesPref = {
  payment: boolean
  requests: boolean
  cart: boolean
  app_access: boolean
}

export type LegalConsentPrefs = {
  mode: RequireCgvConsent
  gates: LegalConsentGatesPref
  snooze_days: number
}

const DEFAULT_LEGAL_CONSENT_GATES: LegalConsentGatesPref = {
  payment: true,
  requests: true,
  cart: false,
  app_access: false,
}

const DEFAULT_SNOOZE_DAYS = 7

function normalizeLegalConsentMode(value: unknown): RequireCgvConsent {
  return value === 'optional' || value === 'off' || value === 'required' ? value : 'required'
}

function boolGate(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

/**
 * Resolves brand `ui_preferences.legal_consent` with legacy `require_cgv_consent` fallback
 * (mirrors backend LegalConsentPreferences).
 */
export function legalConsentFromSnapshot(
  snapshot: BrandAuthenticatedSnapshot | undefined,
): LegalConsentPrefs {
  const ui = snapshot?.ui_preferences
  const hasLegacy = ui != null && Object.prototype.hasOwnProperty.call(ui, 'require_cgv_consent')
  const legacyMode = hasLegacy ? normalizeLegalConsentMode(ui.require_cgv_consent) : 'off'

  const raw = ui?.legal_consent
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return {
      mode: legacyMode,
      gates:
        legacyMode === 'off'
          ? { payment: false, requests: false, cart: false, app_access: false }
          : { ...DEFAULT_LEGAL_CONSENT_GATES },
      snooze_days: DEFAULT_SNOOZE_DAYS,
    }
  }

  const record = raw as Record<string, unknown>
  const mode = Object.prototype.hasOwnProperty.call(record, 'mode')
    ? normalizeLegalConsentMode(record.mode)
    : legacyMode

  const gatesIn =
    typeof record.gates === 'object' && record.gates !== null && !Array.isArray(record.gates)
      ? (record.gates as Record<string, unknown>)
      : {}

  const snoozeRaw = record.snooze_days
  let snoozeDays = DEFAULT_SNOOZE_DAYS
  if (typeof snoozeRaw === 'number' && Number.isFinite(snoozeRaw)) {
    snoozeDays = Math.trunc(snoozeRaw)
  } else if (typeof snoozeRaw === 'string' && /^\d+$/.test(snoozeRaw.trim())) {
    snoozeDays = Number.parseInt(snoozeRaw.trim(), 10)
  }
  if (snoozeDays < 1) {
    snoozeDays = DEFAULT_SNOOZE_DAYS
  }

  return {
    mode,
    gates:
      mode === 'off'
        ? { payment: false, requests: false, cart: false, app_access: false }
        : {
            payment: boolGate(gatesIn.payment, true),
            requests: boolGate(gatesIn.requests, true),
            cart: boolGate(gatesIn.cart, false),
            app_access: boolGate(gatesIn.app_access, false),
          },
    snooze_days: snoozeDays,
  }
}

/**
 * Alias of `legalConsentFromSnapshot(...).mode` for T7.22 call sites.
 * Prefer `legalConsentFromSnapshot` / API status for new code.
 */
export function requireCgvConsentFromSnapshot(
  snapshot: BrandAuthenticatedSnapshot | undefined,
): RequireCgvConsent {
  return legalConsentFromSnapshot(snapshot).mode
}

/**
 * CR-08: admin-configurable landing route (brand_settings.ui_preferences.default_landing_route).
 * Only a fixed set of internal module roots is allowed - the value is an admin key, never a raw
 * URL, so it can never become an open redirect. Returns the target path, or `null` for the default
 * home ('/') and for any absent/unknown value (safe fallback while the BE field is not yet emitted).
 */
const LANDING_ROUTES: Record<string, string> = {
  reservation: '/reservation/properties',
  ticketing: '/ticketing',
  lottery: '/lottery',
  notifications: '/notifications',
}

export function landingRouteFromSnapshot(snapshot: BrandAuthenticatedSnapshot | undefined): string | null {
  const raw = snapshot?.ui_preferences?.default_landing_route
  return typeof raw === 'string' ? (LANDING_ROUTES[raw] ?? null) : null
}

/**
 * AV-X03: DPO email from brand_settings.ui_preferences.dpo_email (Filament Brand page).
 */
export function dpoEmailFromSnapshot(snapshot: BrandAuthenticatedSnapshot | undefined): string | null {
  const raw = snapshot?.ui_preferences?.dpo_email
  if (typeof raw !== 'string') {
    return null
  }
  const email = raw.trim()
  return email.includes('@') ? email : null
}

/** Display timezone from brand_settings.ui_preferences.timezone (IANA). */
export function timezoneFromSnapshot(snapshot: BrandAuthenticatedSnapshot | null | undefined): string | null {
  const raw = snapshot?.ui_preferences?.timezone
  if (typeof raw !== 'string') {
    return null
  }
  const tz = raw.trim()
  return tz !== '' ? tz : null
}

function trimmedText(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : ''
}

function colorsObjectHasRenderableValues(colors: Record<string, unknown> | undefined): boolean {
  if (colors === undefined) {
    return false
  }

  return Object.values(colors).some((v) => typeof v === 'string' && v.trim() !== '')
}

/** Après connexion la source autoritative est `/brand`. */
export function mergeBrandAppearanceForShell(
  public_: BrandPublicAppearance | undefined,
  authenticated: BrandAuthenticatedSnapshot | undefined,
): BrandShellAppearance | undefined {
  if (public_ === undefined && authenticated === undefined) {
    return undefined
  }

  const nameFromAuth = trimmedText(authenticated?.name)
  const nameFromPublic = trimmedText(public_?.name)

  let mergedName: string | undefined
  if (nameFromAuth !== '') {
    mergedName = nameFromAuth
  } else if (nameFromPublic !== '') {
    mergedName = nameFromPublic
  } else {
    mergedName = authenticated?.name !== undefined ? authenticated?.name : public_?.name
  }

  const authColors = authenticated?.colors
  let mergedColors: Record<string, unknown> | undefined
  if (
    authenticated !== undefined &&
    authColors !== undefined &&
    typeof authColors === 'object' &&
    !Array.isArray(authColors) &&
    colorsObjectHasRenderableValues(authColors as Record<string, unknown>)
  ) {
    mergedColors = authColors as Record<string, unknown>
  } else if (public_?.colors !== undefined) {
    mergedColors = public_.colors as Record<string, unknown>
  }

  const showLogo = isShowLogoEnabled(authenticated ?? public_)
  const rawLogoUrl =
    authenticated !== undefined ? (authenticated.logo_url ?? public_?.logo_url ?? null) : (public_?.logo_url ?? null)
  const rawLogoDarkUrl =
    authenticated !== undefined
      ? (authenticated.logo_dark_url ?? public_?.logo_dark_url ?? null)
      : (public_?.logo_dark_url ?? null)
  const mergedLogoUrl = showLogo ? rawLogoUrl : null
  const mergedLogoDarkUrl = showLogo ? rawLogoDarkUrl : null
  const mergedLogoFaviconUrl =
    authenticated !== undefined
      ? (authenticated.logo_favicon_url ?? public_?.logo_favicon_url ?? null)
      : (public_?.logo_favicon_url ?? null)

  const authTypography = authenticated?.typography
  const mergedTypography =
    authTypography !== undefined && Object.keys(authTypography).length > 0 ? authTypography : public_?.typography

  const mergedRadiusPreset = authenticated?.radius_preset ?? public_?.radius_preset

  const authCustomTokens = authenticated?.custom_tokens
  const mergedCustomTokens =
    authCustomTokens !== undefined && Object.keys(authCustomTokens).length > 0
      ? authCustomTokens
      : public_?.custom_tokens

  return {
    name: mergedName,
    theme_slug: authenticated?.theme_slug ?? public_?.theme_slug,
    theme_palette_override_enabled:
      authenticated?.theme_palette_override_enabled ?? public_?.theme_palette_override_enabled,
    logo_url: mergedLogoUrl,
    logo_dark_url: mergedLogoDarkUrl,
    logo_favicon_url: mergedLogoFaviconUrl,
    colors: mergedColors,
    typography: mergedTypography,
    radius_preset: mergedRadiusPreset,
    custom_tokens: mergedCustomTokens,
  }
}

export const brandApiResponseSchema = brandAuthenticatedApiResponseSchema

export type BrandSnapshot = BrandShellAppearance & {
  portal_navigation?: PortalNavigationItem[]
  contact?: Record<string, unknown>
}
